import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Enums, Json, Tables } from "@how/database";
import { fromSavedMenuItems, toSavedMenuItems, type MenuDraftItem } from "@how/website-core";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export type MenuName = Enums<"navigation_menu">;
export const MENUS: readonly MenuName[] = ["header", "footer"];

export interface MenuState {
  readonly menu: MenuName;
  readonly draft: readonly MenuDraftItem[];
  /** Null until the menu has been published once. */
  readonly publishedAt: string | null;
  readonly draftUpdatedAt: string | null;
  /** True when the draft has moved on since it was last published. */
  readonly changed: boolean;
}

type MenuRow = Pick<
  Tables<"navigation_menus">,
  "menu" | "draft_items" | "published_items" | "published_at" | "draft_updated_at"
>;

/** Both menus, whether or not the church has ever saved them. A menu that has no row is simply empty. */
export function useMenus() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["menus", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<MenuState[]> => {
      const { data, error } = await supabase
        .from("navigation_menus")
        .select("menu, draft_items, published_items, published_at, draft_updated_at")
        .eq("tenant_id", tenantId ?? "");
      if (error) throw error;
      const rows = new Map<string, MenuRow>(data.map((row) => [row.menu, row]));
      return MENUS.map((menu) => {
        const row = rows.get(menu);
        if (row === undefined) {
          return { menu, draft: [], publishedAt: null, draftUpdatedAt: null, changed: false };
        }
        return {
          menu,
          draft: fromSavedMenuItems(row.draft_items),
          publishedAt: row.published_at,
          draftUpdatedAt: row.draft_updated_at,
          changed:
            row.published_at !== null &&
            new Date(row.draft_updated_at).getTime() > new Date(row.published_at).getTime(),
        };
      });
    },
  });
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

function useMenuInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["menus", tenantId] });
  };
}

export function useSaveMenu() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useMenuInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly menu: MenuName;
      readonly items: readonly MenuDraftItem[];
    }) => {
      const { error } = await supabase.rpc("save_navigation", {
        p_tenant_id: tenantId ?? "",
        p_menu: input.menu,
        p_items: toSavedMenuItems(input.items) as unknown as Json,
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

/** Publishing resolves every page link to the address that page currently lives at. */
export function usePublishMenu() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useMenuInvalidation();
  return useMutation({
    mutationFn: async (menu: MenuName) => {
      const { error } = await supabase.rpc("publish_navigation", {
        p_tenant_id: tenantId ?? "",
        p_menu: menu,
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}
